import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Trash2, Loader2, Users, ExternalLink, Edit, Lock } from "lucide-react";
import { format } from "date-fns";
import { Link } from "wouter";
import type { Account, TrustBeneficiary, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { StructureDatePicker } from "@/components/ui/structure-date-picker";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { ContactFormModal } from "@/components/contact-form-modal";

interface TrustBeneficiariesPanelProps {
  account: Account;
}

// API join fields not in the Drizzle schema type (returned by beneficiaries endpoint)
interface BeneficiaryWithContact extends TrustBeneficiary {
  contactFirstName?: string;
  contactLastName?: string;
}

interface ToggleUboResponse {
  uboRemoved?: boolean;
}

const BENEFICIARY_CLASS_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "contingent", label: "Contingent" },
  { value: "default", label: "Default" },
];

const ENTITLEMENT_TYPE_OPTIONS = [
  { value: "income_only", label: "Income Only" },
  { value: "capital_only", label: "Capital Only" },
  { value: "income_and_capital", label: "Income & Capital" },
];

export function TrustBeneficiariesPanel({ account }: TrustBeneficiariesPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [beneficiaryClass, setBeneficiaryClass] = useState<string | undefined>();
  const [unitCount, setUnitCount] = useState<string>("");
  const [unitPercentage, setUnitPercentage] = useState<string>("");
  const [entitlementType, setEntitlementType] = useState<string>("income_and_capital");
  const [isActive, setIsActive] = useState(true);
  const [effectiveFrom, setEffectiveFrom] = useState<Date | undefined>();
  const [effectiveTo, setEffectiveTo] = useState<Date | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // ISSUE-163 FIX: Add state for create contact dialog
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  // ISSUE-057 FIX: Add state for edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState<BeneficiaryWithContact | null>(null);
  const [editBeneficiaryClass, setEditBeneficiaryClass] = useState<string | undefined>();
  const [editUnitCount, setEditUnitCount] = useState<string>("");
  const [editUnitPercentage, setEditUnitPercentage] = useState<string>("");
  const [editEntitlementType, setEditEntitlementType] = useState<string>("income_and_capital");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editEffectiveFrom, setEditEffectiveFrom] = useState<Date | undefined>();
  const [editEffectiveTo, setEditEffectiveTo] = useState<Date | undefined>();
  // #448: UBO checkbox state for Add and Edit dialogs
  const [isAddUbo, setIsAddUbo] = useState(false);
  const [editIsUbo, setEditIsUbo] = useState(false);
  // SPRINT-26 F006: UBO toggle — confirm dialog state
  const [uboConfirmBeneficiary, setUboConfirmBeneficiary] = useState<BeneficiaryWithContact | null>(null);
  const { toast } = useToast();

  const { data: beneficiaries = [], isLoading } = useQuery<BeneficiaryWithContact[]>({
    queryKey: ["/api/crm/accounts", account.id, "beneficiaries"],
    enabled: account.entityType === "trust",
  });

  // ISSUE-050 FIX: Fetch contacts filtered by client group
  const { data: _contacts = [] } = useQuery<Contact[]>({
    queryKey: account.clientGroupId
      ? ["/api/crm/contacts", { clientGroupId: account.clientGroupId }]
      : ["/api/crm/contacts"],
    enabled: isAddDialogOpen,
  });

  // ISSUE-050: Get existing beneficiary contact IDs to prevent duplicates
  const existingBeneficiaryContactIds = useMemo(() => {
    return beneficiaries
      .filter((b) => b.beneficiaryContactId)
      .map((b) => b.beneficiaryContactId as string);
  }, [beneficiaries]);

  // SPRINT-2 Gap-Fill: beneficiaryName removed from schema - name is derived from linked contact
  const addMutation = useMutation({
    mutationFn: async (data: {
      beneficiaryContactId: string; // REQUIRED per BR-BEN-001
      beneficiaryClass?: string;
      unitCount?: number;
      unitPercentage?: string;
      entitlementType?: string;
      isActive?: number;
      effectiveFrom?: string;
      effectiveTo?: string;
      isBeneficialOwner?: boolean; // #448: UBO flag — triggers toggle-ubo after creation
    }) => {
      const { isBeneficialOwner: addAsUbo, ...rest } = data;
      const response = await apiRequest("POST", `/api/crm/accounts/${account.id}/beneficiaries`, {
        trustAccountId: account.id,
        ...rest,
      });
      // #448: If UBO checked, call toggle-ubo on the newly created beneficiary
      if (addAsUbo) {
        const beneficiary = await response.json();
        if (beneficiary?.id) {
          await apiRequest(
            "POST",
            `/api/crm/accounts/${account.id}/beneficiaries/${beneficiary.id}/toggle-ubo`,
            { isUbo: true }
          );
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficiaries"] });
      toast({ title: "Beneficiary added", description: "The beneficiary has been added successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add beneficiary", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/beneficiaries/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficiaries"] });
      toast({ title: "Beneficiary removed", description: "The beneficiary has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove beneficiary", variant: "destructive" });
    },
  });

  // ISSUE-057 FIX: Edit mutation for beneficiaries
  const editMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      beneficiaryClass?: string;
      unitCount?: number;
      unitPercentage?: string;
      entitlementType?: string;
      isActive?: number;
      effectiveFrom?: string | null;
      effectiveTo?: string | null;
      isBeneficialOwner?: boolean; // #448: UBO flag — triggers toggle-ubo if changed
    }) => {
      const { id, isBeneficialOwner: newUboState, ...updateData } = data;
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/beneficiaries/${id}`, updateData);
      // #448: Sync UBO state if provided and different from current
      if (newUboState !== undefined && editingBeneficiary) {
        const currentUbo = editingBeneficiary.isBeneficialOwner === 1;
        if (newUboState !== currentUbo) {
          await apiRequest(
            "POST",
            `/api/crm/accounts/${account.id}/beneficiaries/${id}/toggle-ubo`,
            { isUbo: newUboState }
          );
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficiaries"] });
      toast({ title: "Beneficiary updated", description: "The beneficiary has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update beneficiary", variant: "destructive" });
    },
  });

  // SPRINT-26 F006: UBO toggle mutation with optimistic update
  const uboToggleMutation = useMutation({
    mutationFn: async ({ beneficiaryId, isUbo }: { beneficiaryId: string; isUbo: boolean }) => {
      const response = await apiRequest(
        "POST",
        `/api/crm/accounts/${account.id}/beneficiaries/${beneficiaryId}/toggle-ubo`,
        { isUbo }
      );
      const body = (await response.json()) as ToggleUboResponse;
      return body;
    },
    onMutate: async ({ beneficiaryId, isUbo }) => {
      // Optimistically update the cached beneficiaries list
      await queryClient.cancelQueries({
        queryKey: ["/api/crm/accounts", account.id, "beneficiaries"],
      });
      const previous = queryClient.getQueryData<BeneficiaryWithContact[]>([
        "/api/crm/accounts",
        account.id,
        "beneficiaries",
      ]);
      queryClient.setQueryData<BeneficiaryWithContact[]>(
        ["/api/crm/accounts", account.id, "beneficiaries"],
        (old = []) =>
          old.map((b) =>
            b.id === beneficiaryId
              ? { ...b, isBeneficialOwner: isUbo ? 1 : 0, linkedBeneficialOwnerId: isUbo ? b.linkedBeneficialOwnerId : null }
              : b
          )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Revert optimistic update
      if (context?.previous) {
        queryClient.setQueryData(
          ["/api/crm/accounts", account.id, "beneficiaries"],
          context.previous
        );
      }
      toast({
        title: "Error",
        description: "Failed to update beneficial owner status.",
        variant: "destructive",
      });
    },
    onSuccess: (data, { isUbo }) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/accounts", account.id, "beneficiaries"],
      });
      if (isUbo) {
        toast({ title: "Beneficial owner record created", description: "This beneficiary is now linked as a beneficial owner." });
      } else {
        if (data?.uboRemoved) {
          toast({ title: "Beneficial owner record removed", description: "The beneficial owner record has been removed." });
        } else {
          toast({ title: "Beneficial owner status updated", description: "Beneficiary was unticked, but no linked auto-created beneficial owner record was removed." });
        }
      }
    },
  });

  const handleUboCheckboxChange = (beneficiary: BeneficiaryWithContact, checked: boolean) => {
    if (checked) {
      // Toggle ON — no confirmation needed
      uboToggleMutation.mutate({ beneficiaryId: beneficiary.id, isUbo: true });
    } else {
      // Toggle OFF — show confirmation dialog
      setUboConfirmBeneficiary(beneficiary);
    }
  };

  const handleUboConfirmRemove = () => {
    if (!uboConfirmBeneficiary) return;
    uboToggleMutation.mutate({ beneficiaryId: uboConfirmBeneficiary.id, isUbo: false });
    setUboConfirmBeneficiary(null);
  };

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedContactId(null);
    setBeneficiaryClass(undefined);
    setUnitCount("");
    setUnitPercentage("");
    setEntitlementType("income_and_capital");
    setIsActive(true);
    setEffectiveFrom(undefined);
    setEffectiveTo(undefined);
    setIsAddUbo(false); // #448: reset UBO checkbox
  };

  // ISSUE-057 FIX: Open edit dialog with beneficiary data
  const handleOpenEditDialog = (beneficiary: BeneficiaryWithContact) => {
    setEditingBeneficiary(beneficiary);
    setEditBeneficiaryClass(beneficiary.beneficiaryClass || undefined);
    setEditUnitCount(beneficiary.unitCount?.toString() || "");
    setEditUnitPercentage(beneficiary.unitPercentage || "");
    setEditEntitlementType(beneficiary.entitlementType || "income_and_capital");
    setEditIsActive(beneficiary.isActive === 1);
    setEditEffectiveFrom(beneficiary.effectiveFrom ? new Date(beneficiary.effectiveFrom) : undefined);
    setEditEffectiveTo(beneficiary.effectiveTo ? new Date(beneficiary.effectiveTo) : undefined);
    setEditIsUbo(beneficiary.isBeneficialOwner === 1); // #448: pre-populate UBO checkbox
    setIsEditDialogOpen(true);
  };

  // ISSUE-057 FIX: Close edit dialog and reset state
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingBeneficiary(null);
    setEditBeneficiaryClass(undefined);
    setEditUnitCount("");
    setEditUnitPercentage("");
    setEditEntitlementType("income_and_capital");
    setEditIsActive(true);
    setEditEffectiveFrom(undefined);
    setEditEffectiveTo(undefined);
    setEditIsUbo(false); // #448: reset UBO checkbox
  };

  // ISSUE-057 FIX: Submit edit
  const handleEdit = () => {
    if (!editingBeneficiary) return;

    editMutation.mutate({
      id: editingBeneficiary.id,
      beneficiaryClass: editBeneficiaryClass,
      unitCount: editUnitCount ? parseInt(editUnitCount, 10) : undefined,
      unitPercentage: editUnitPercentage || undefined,
      entitlementType: editEntitlementType,
      isActive: editIsActive ? 1 : 0,
      effectiveFrom: editEffectiveFrom ? format(editEffectiveFrom, "yyyy-MM-dd") : null,
      effectiveTo: editEffectiveTo ? format(editEffectiveTo, "yyyy-MM-dd") : null,
      isBeneficialOwner: editIsUbo, // #448: sync UBO state on save
    });
  };

  // ISSUE-050 FIX: Simplified validation - contact selection is now required
  const handleAdd = () => {
    if (!selectedContactId) {
      toast({
        title: "Validation Error",
        description: "Please select a contact to add as a beneficiary.",
        variant: "destructive"
      });
      return;
    }

    // SPRINT-2 Gap-Fill: beneficiaryName removed from mutation payload per BR-BEN-001
    addMutation.mutate({
      beneficiaryContactId: selectedContactId,
      beneficiaryClass: beneficiaryClass,
      unitCount: unitCount ? parseInt(unitCount, 10) : undefined,
      unitPercentage: unitPercentage || undefined,
      entitlementType: entitlementType,
      isActive: isActive ? 1 : 0,
      effectiveFrom: effectiveFrom ? format(effectiveFrom, "yyyy-MM-dd") : undefined,
      effectiveTo: effectiveTo ? format(effectiveTo, "yyyy-MM-dd") : undefined,
      isBeneficialOwner: isAddUbo, // #448: UBO flag
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatEntitlementType = (type: string | null) => {
    if (!type) return "-";
    return type.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  const formatBeneficiaryClass = (cls: string | null) => {
    if (!cls) return "-";
    return cls.charAt(0).toUpperCase() + cls.slice(1);
  };

  if (account.entityType !== "trust") {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="trust-beneficiaries-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">BENEFICIARIES</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-beneficiary"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Beneficiary
        </Button>
      </div>

      {beneficiaries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md bg-white">
          <Users className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No beneficiaries yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Beneficiary" to add a beneficiary to this trust
          </p>
        </div>
      ) : (
        <div className="border rounded-md bg-white">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Beneficiary Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[40px]" title="Also a Beneficial Owner">UBO</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Class</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Unit %</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Entitlement Type</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Active</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Effective From</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Effective To</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {beneficiaries.map((beneficiary) => (
                <TableRow
                  key={beneficiary.id}
                  className={cn(
                    "h-10",
                    beneficiary.isBeneficialOwner === 1 && "border-l-2 border-l-emerald-500"
                  )}
                  data-testid={`row-beneficiary-${beneficiary.id}`}
                >
                  {/* SPRINT-2 Gap-Fill: Display name from contact (beneficiaryName removed) */}
                  <TableCell className="px-2 py-1 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {`${beneficiary.contactFirstName || ''} ${beneficiary.contactLastName || ''}`.trim() || '-'}
                      {beneficiary.isBeneficialOwner === 1 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                data-testid={`badge-ubo-${beneficiary.id}`}
                                className={cn(
                                  "text-[10px] uppercase font-semibold px-1.5 py-0.5 transition-opacity duration-200",
                                  beneficiary.linkedBeneficialOwnerId
                                    ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                                    : "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-100"
                                )}
                                variant="outline"
                              >
                                UBO
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {beneficiary.linkedBeneficialOwnerId
                                ? 'Linked beneficial owner record'
                                : 'Manually marked beneficial owner'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {beneficiary.beneficiaryContactId && (
                        <Link
                          to={`/contacts/${beneficiary.beneficiaryContactId}`}
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </TableCell>
                  {/* SPRINT-26 F006: UBO checkbox column */}
                  <TableCell className="px-2 py-1 w-[40px]">
                    {beneficiary.isBeneficialOwner === 1 && !beneficiary.linkedBeneficialOwnerId ? (
                      // Manual UBO: disabled with lock icon
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center justify-center">
                              <Checkbox
                                data-testid={`checkbox-ubo-${beneficiary.id}`}
                                checked={true}
                                disabled={true}
                                aria-label="Beneficial owner (manually created)"
                              />
                              <Lock className="h-3 w-3 ml-1 text-muted-foreground" data-testid="icon-ubo-locked" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Manually created beneficial owner — manage in Beneficial Owners panel
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Checkbox
                        data-testid={`checkbox-ubo-${beneficiary.id}`}
                        checked={beneficiary.isBeneficialOwner === 1}
                        disabled={uboToggleMutation.isPending && uboToggleMutation.variables?.beneficiaryId === beneficiary.id}
                        onCheckedChange={(checked) =>
                          handleUboCheckboxChange(beneficiary, checked === true)
                        }
                        aria-label="Also a beneficial owner"
                      />
                    )}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatBeneficiaryClass(beneficiary.beneficiaryClass)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {beneficiary.unitPercentage ? `${beneficiary.unitPercentage}%` : "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatEntitlementType(beneficiary.entitlementType)}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge
                      variant={beneficiary.isActive === 1 ? "default" : "secondary"}
                      className="text-xs"
                      data-testid={`badge-active-${beneficiary.id}`}
                    >
                      {beneficiary.isActive === 1 ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(beneficiary.effectiveFrom)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(beneficiary.effectiveTo)}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    {/* ISSUE-057 FIX: Added Edit and Delete buttons */}
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditDialog(beneficiary)}
                        data-testid={`button-edit-beneficiary-${beneficiary.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(beneficiary.id)}
                        data-testid={`button-delete-beneficiary-${beneficiary.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* ISSUE-050 FIX: Add Dialog - contact-only mode (no manual entry) */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add Beneficiary</DialogTitle>
            <DialogDescription>
              Select a contact from the client group to add as a beneficiary to this trust.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Contact Selection - ISSUE-050: Contact-only mode */}
            <div className="space-y-2">
              <Label>Contact <span className="text-destructive">*</span></Label>
              <ContactSelect
                value={selectedContactId}
                onValueChange={setSelectedContactId}
                clientGroupId={account.clientGroupId}
                excludeContactIds={existingBeneficiaryContactIds}
                placeholder="Select a contact from this client group"
                data-testid="select-beneficiary-contact"
              />
              {/* ISSUE-163 FIX: Replace placeholder with actual contact creation dialog trigger */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateContactDialog(true)}
                className="mt-2 w-full"
                data-testid="button-create-new-contact-beneficiary"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Contact
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Beneficiary Class</Label>
                <Select value={beneficiaryClass} onValueChange={setBeneficiaryClass}>
                  <SelectTrigger data-testid="select-beneficiary-class">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {BENEFICIARY_CLASS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entitlement Type</Label>
                <Select value={entitlementType} onValueChange={setEntitlementType}>
                  <SelectTrigger data-testid="select-entitlement-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITLEMENT_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* #447: Unit fields only visible for unit trusts */}
            {account.trustType === "unit" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit Count</Label>
                  <Input
                    type="number"
                    value={unitCount}
                    onChange={(e) => setUnitCount(e.target.value)}
                    placeholder="Optional"
                    data-testid="input-unit-count"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Percentage</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={unitPercentage}
                    onChange={(e) => setUnitPercentage(e.target.value)}
                    placeholder="Optional"
                    data-testid="input-unit-percentage"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <StructureDatePicker
                  value={effectiveFrom}
                  onChange={setEffectiveFrom}
                  testId="input-effective-from"
                />
              </div>
              <div className="space-y-2">
                <Label>Effective To</Label>
                <StructureDatePicker
                  value={effectiveTo}
                  onChange={setEffectiveTo}
                  testId="input-effective-to"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(checked === true)}
                data-testid="checkbox-is-active"
              />
              <Label htmlFor="isActive" className="cursor-pointer">Active</Label>
            </div>
            {/* #448: UBO checkbox in Add dialog */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isAddUbo"
                checked={isAddUbo}
                onCheckedChange={(checked) => setIsAddUbo(checked === true)}
                data-testid="checkbox-add-ubo"
              />
              <Label htmlFor="isAddUbo" className="cursor-pointer">Mark as Beneficial Owner</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedContactId || addMutation.isPending}
              data-testid="button-confirm-add-beneficiary"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Beneficiary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Beneficiary</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this beneficiary? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-beneficiary"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ISSUE-057 FIX: Edit Beneficiary Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Beneficiary</DialogTitle>
            {/* SPRINT-2 Gap-Fill: Display name from contact */}
            <DialogDescription>
              Update beneficiary details for {`${editingBeneficiary?.contactFirstName || ''} ${editingBeneficiary?.contactLastName || ''}`.trim() || 'this beneficiary'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Beneficiary Class</Label>
                <Select value={editBeneficiaryClass} onValueChange={setEditBeneficiaryClass}>
                  <SelectTrigger data-testid="edit-select-beneficiary-class">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {BENEFICIARY_CLASS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entitlement Type</Label>
                <Select value={editEntitlementType} onValueChange={setEditEntitlementType}>
                  <SelectTrigger data-testid="edit-select-entitlement-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITLEMENT_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* #447: Unit fields only visible for unit trusts */}
            {account.trustType === "unit" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit Count</Label>
                <Input
                  type="number"
                  value={editUnitCount}
                  onChange={(e) => setEditUnitCount(e.target.value)}
                  placeholder="Optional"
                  data-testid="edit-input-unit-count"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Percentage</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editUnitPercentage}
                  onChange={(e) => setEditUnitPercentage(e.target.value)}
                  placeholder="Optional"
                  data-testid="edit-input-unit-percentage"
                />
              </div>
            </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <StructureDatePicker
                  value={editEffectiveFrom}
                  onChange={setEditEffectiveFrom}
                  testId="edit-input-effective-from"
                />
                {editEffectiveFrom && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditEffectiveFrom(undefined)}
                    className="w-full"
                  >
                    Clear Date
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label>Effective To</Label>
                <StructureDatePicker
                  value={editEffectiveTo}
                  onChange={setEditEffectiveTo}
                  testId="edit-input-effective-to"
                />
                {editEffectiveTo && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditEffectiveTo(undefined)}
                    className="w-full"
                  >
                    Clear Date
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="editIsActive"
                checked={editIsActive}
                onCheckedChange={(checked) => setEditIsActive(checked === true)}
                data-testid="edit-checkbox-is-active"
              />
              <Label htmlFor="editIsActive" className="cursor-pointer">Active</Label>
            </div>
            {/* #448: UBO checkbox in Edit dialog */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="editIsUbo"
                checked={editIsUbo}
                onCheckedChange={(checked) => setEditIsUbo(checked === true)}
                data-testid="edit-checkbox-ubo"
              />
              <Label htmlFor="editIsUbo" className="cursor-pointer">Mark as Beneficial Owner</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={editMutation.isPending}
              data-testid="button-confirm-edit-beneficiary"
            >
              {editMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SPRINT-26 F006: UBO removal confirmation dialog */}
      <AlertDialog
        open={!!uboConfirmBeneficiary}
        onOpenChange={(open) => { if (!open) setUboConfirmBeneficiary(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove beneficial owner record?</AlertDialogTitle>
            <AlertDialogDescription>
              {`Remove beneficial owner record for ${`${uboConfirmBeneficiary?.contactFirstName || ''} ${uboConfirmBeneficiary?.contactLastName || ''}`.trim() || 'this beneficiary'}?`}
              {" "}This will remove the auto-created beneficial owner record. Manually-created records will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleUboConfirmRemove}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ISSUE-163 FIX: Contact Creation Modal - auto-links to client group and referring partner */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        onContactCreated={(newContact: Contact) => {
          // Select the newly created contact in the dropdown
          setSelectedContactId(newContact.id);
          setShowCreateContactDialog(false);
          // Refresh contacts list
          queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
          toast({
            title: "Contact created",
            description: "The contact has been created and selected. Click 'Add Beneficiary' to complete."
          });
        }}
        defaultClientGroupId={account.clientGroupId}
      />
    </div>
  );
}
